import useUserStore, { useIndexStore } from "@/state/user/useUserStore";
import React, { useEffect, useRef, useState } from "react";
import { m } from "motion/react";
import {
  ArrowBigLeft,
  FlipHorizontal2,
  ImagePlus,
  Layers,
  Sparkle,
  Type,
} from "lucide-react";
import {
  CustomTab,
  TitleShadow,
  TitleShadowBullas,
  TitleShadowHenlo,
  MobileBackBtn,
} from "@/components/styles";
import AssetComponent from "@/components/ui/asset-component";
import MemeCanvas from "@/components/ui/meme-canvas";
import { Asset } from "@/types/types";
import { domToPng } from "modern-screenshot";
import { toast } from "sonner";
import { UploadGallery } from "@/app/actions/upload-gallery";
import { useBackHandler } from "@/lib/hooks/useBackHanlder";
import LayersComponent from "@/components/ui/layers-component";
import TextComponent from "@/components/ui/text-component";

export const PostPageMobile = ({
  color,
  assets,
  customFont,
  twitter,
  defaultImg,
  tweetText,
  websiteLink,
  replyTo,
}: {
  color: string;
  assets: Asset[];
  customFont?: string;
  twitter?: string;
  defaultImg?: string;
  tweetText?: string;
  websiteLink?: string;
  replyTo?: string;
}) => {
  const [
    setSelectedImage,
    setAssets,
    textFields,
    removeTextField,
    flipImage,
    setFlipImage,
    history,
    setHistory,
    step,
    setStep,
    setUploadAssets,
    canvasIsLoading,
    uploadAssets,
    selectedAssets,
    hasUploadedGallery,
    setHasUploadedGallery,
  ] = useUserStore((state) => [
    state.setSelectedImage,
    state.setAssets,
    state.textFields,
    state.removeTextField,
    state.flipImage,
    state.setFlipImage,
    state.history,
    state.setHistory,
    state.step,
    state.setStep,
    state.setUploadAssets,
    state.canvasIsLoading,
    state.uploadAssets,
    state.assets,
    state.hasUploadedGallery,
    state.setHasUploadedGallery,
  ]);

  const [lastUploadAssetIndex, setLastUploadAssetIndex] = useIndexStore(
    (state) => [state.lastUploadAssetIndex, state.setLastUploadAssetIndex]
  );

  const divRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const parentVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.4,
        delayChildren: 0.3,
        staggerChildren: 0.3,
        type: "bounce",
      },
    },
  };

  const childVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  };

  const fileAssetInputRef = useRef<HTMLInputElement>(null);

  const handleUploadAssetContainer = () => {
    if (fileAssetInputRef.current !== null) {
      fileAssetInputRef.current.click();
    }
  };

  const allFieldsLength =
    textFields.length + selectedAssets.length + uploadAssets.length;

  const handleAssetUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      const file = event.target.files[0];
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const ratio = width / height;
        const id = crypto.randomUUID();
        setUploadAssets([
          ...uploadAssets,
          {
            id: id,
            img: URL.createObjectURL(file),
            ratio,
            flipHorizontal: false,
            flipVertical: false,
            zIndex: allFieldsLength + 1,
            index: lastUploadAssetIndex + 1,
          },
        ]);
        setLastUploadAssetIndex(lastUploadAssetIndex + 1);
      };
      if (file instanceof File) {
        img.src = URL.createObjectURL(file);
      }
    }
  };

  const undo = () => {
    if (step > 0) {
      setStep(step - 1); // Move the step back
      restoreState(history[step - 1]); // Restore the canvas to the previous state
    }
  };

  const redo = () => {
    if (step < history.length - 1) {
      setStep(step + 1); // Move the step forward
      restoreState(history[step + 1]); // Restore the canvas to the next state
    }
  };

  const restoreState = (canvasDataUrl: string | null) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        if (canvasDataUrl === null) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        } else {
          const img = new window.Image();
          img.src = canvasDataUrl;
          img.onload = () => {
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
        }
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        setStep(0);
        setHistory([null]);
      }
    }
  };

  const [mode, setMode] = useState<"asset" | "draw" | "layers" | "text">("asset");

  useEffect(() => {
    if (defaultImg) {
      setSelectedImage(defaultImg);
    }
  }, []);

  const backHandler = useBackHandler();

  const [isProcessing, setIsProcessing] = useState(false);

  const convertToPng = async () => {
    if (divRef.current !== null && !isProcessing) {
      setIsProcessing(true);

      try {
        const dataUrl = await domToPng(divRef.current, {
          scale: 2,
          backgroundColor: null,
        });

        // Create a temporary link element to trigger download
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "meme.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error capturing image:", error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginBottom: "16px",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "start",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                marginBottom: "4px",
              }}
            >
              {customFont === "henlo" && (
                <TitleShadowHenlo style={{ margin: 0, marginBottom: "4px" }}>
                  Customize
                </TitleShadowHenlo>
              )}
              {customFont === "bullas" && (
                <TitleShadowBullas style={{ margin: 0, marginBottom: "4px" }}>
                  Customize
                </TitleShadowBullas>
              )}
              {!customFont && (
                <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
                  Customize
                </TitleShadow>
              )}
              <div
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontFamily: "var(--font-cabin-condensed)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "black",
                }}
                onClick={backHandler}
              >
                <MobileBackBtn
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ArrowBigLeft size={20} />
                </MobileBackBtn>
              </div>
            </div>
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-cabin-condensed)",
              }}
            >
              Tap on assets to add them to your image. Drag added assets around
              to position them. Go to layers tab to flip and remove assets.
            </p>
          </div>

          {/* <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 0,
            }}
          >
            {!defaultImg ? (
              <div
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontFamily: "var(--font-cabin-condensed)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "black",
                }}
                onClick={backHandler}
              >
                <UploadImageContainer
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ArrowBigLeft size={20} />
                  Back
                </UploadImageContainer>
              </div>
            ) : (
              <Link
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontFamily: "var(--font-cabin-condensed)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "black",
                }}
                href="/"
              >
                <UploadImageContainer
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ArrowBigLeft size={20} />
                  Back
                </UploadImageContainer>
              </Link>
            )}
          </div> */}
        </div>
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column-reverse",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxHeight: "600px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ width: "100%", display: "flex", gap: "8px" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: "12px",
                  boxSizing: "border-box",
                  borderRadius: "0.25rem",
                  color: "#000000",
                  background: "#ccc",
                  cursor: "pointer",
                  fontFamily: "var(--font-cabin-condensed)",
                  fontSize: "1rem",
                  boxShadow: "2px 2px 0px 0px #000000",
                  border: "4px solid #000000",
                  position: "relative",
                }}
              >
                <CustomTab
                  mode={"asset"}
                  currentMode={mode}
                  onClick={() => setMode("asset")}
                >
                  <ImagePlus />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Assets
                  </p>
                </CustomTab>
                <CustomTab
                  mode={"text"}
                  currentMode={mode}
                  onClick={() => setMode("text")}
                >
                  <Type />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Text
                  </p>
                </CustomTab>
              </div>
              <m.div
                whileHover={{
                  scale: 1.02,
                  transition: {
                    duration: 0.15,
                    ease: "linear",
                  },
                }}
                whileTap={{ scale: 0.99 }}
                style={{
                  width: "9rem",
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: "12px",
                  boxSizing: "border-box",
                  borderRadius: "0.25rem",
                  color: "#000000",
                  background: "#ccc",
                  cursor: "pointer",
                  fontFamily: "var(--font-cabin-condensed)",
                  fontSize: "1rem",
                  boxShadow: "2px 2px 0px 0px #000000",
                  border: "4px solid #000000",
                  position: "relative",
                }}
              >
                <button
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "8px",
                    border: "none",
                    boxSizing: "border-box",
                    color: "#000000",
                    background: "#ccc",
                    cursor: "pointer",
                    fontFamily: "var(--font-cabin-condensed)",
                    fontSize: "1rem",
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  onClick={() => setMode("layers")}
                >
                  <Layers />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Layers
                  </p>
                </button>
              </m.div>
            </div>
            {(() => {
              switch (mode) {
                case "asset":
                  return (
                    <AssetComponent
                      noProjectFolder={true}
                      noTabs={true}
                      parentVariants={parentVariants}
                      childVariants={childVariants}
                      color={color}
                      assets={[
                        {
                          tab: "head",
                          items: assets[0].assets,
                          icon: assets[0].icon,
                        },
                        {
                          tab: "clothing",
                          items: assets[1].assets,
                          icon: assets[1].icon,
                        },
                        {
                          tab: "face",
                          items: assets[2].assets,
                          icon: assets[2].icon,
                        },
                        {
                          tab: "eye",
                          items: assets[3].assets,
                          icon: assets[3].icon,
                        },
                        {
                          tab: "misc",
                          items: assets[4].assets,
                          icon: assets[4].icon,
                        },
                      ]}
                    />
                  );
                case "layers":
                  return (
                    <LayersComponent
                      parentVariants={parentVariants}
                      color={color}
                    />
                  );
                case "text":
                  return (
                    <TextComponent
                      parentVariants={parentVariants}
                      childVariants={childVariants}
                      color={color}
                    />
                  );
                default:
                  return <div></div>;
              }
            })()}
          </div>
          <div
            style={{
              height: "100%",
              width: "100%",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "items-start",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "12px",
                padding: "0px 0px 16px 0px",
                boxSizing: "border-box",
                height: "4rem",
              }}
            >
              <div
                style={{
                  width: "6rem",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                }}
              >
                <m.button
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "8px",
                    borderRadius: "0.25rem",
                    boxSizing: "border-box",
                    color: "#000000",
                    background: "#ccc",
                    cursor: "pointer",
                    fontFamily: "var(--font-cabin-condensed)",
                    fontSize: "1rem",
                    boxShadow: "2px 2px 0px 0px #000000",
                    border: "4px solid #000000",
                  }}
                  onClick={() => setFlipImage(!flipImage)}
                >
                  <FlipHorizontal2 />
                </m.button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  height: "100%",
                }}
              >
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "6rem", height: "100%" }}
                >
                  <button
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "8px",
                      borderRadius: "0.25rem",
                      boxSizing: "border-box",
                      color: "#000000",
                      background: "#ffffff",
                      cursor: "pointer",
                      fontFamily: "var(--font-cabin-condensed)",
                      fontSize: "1rem",
                      boxShadow: "2px 2px 0px 0px #000000",
                      border: "4px solid #000000",
                    }}
                    onClick={() => {
                      setAssets([]);
                      textFields.forEach((textField) =>
                        removeTextField(textField.id)
                      );
                      clearCanvas();
                    }}
                  >
                    Clear
                  </button>
                </m.div>
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "8rem", height: "100%" }}
                >
                  <button
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "8px",
                      borderRadius: "0.25rem",
                      boxSizing: "border-box",
                      background:
                        isProcessing || canvasIsLoading ? `${color}80` : color,
                      color: "#000",
                      cursor: isProcessing ? "default" : "pointer",
                      fontFamily: "var(--font-cabin-condensed)",
                      fontSize: "1rem",
                      boxShadow: "2px 2px 0px 0px #000000",
                      border: "4px solid #000000",
                      opacity: isProcessing ? 0.7 : 1,
                      transition: "opacity 0.2s, background 0.2s",
                      WebkitTapHighlightColor: "transparent",
                      touchAction: "manipulation",
                    }}
                    onClick={convertToPng}
                    disabled={isProcessing || canvasIsLoading}
                  >
                    {isProcessing ? "Processing..." : "Download"}
                  </button>
                </m.div>
              </div>
            </div>

            <MemeCanvas
              mode={mode}
              divRef={divRef}
              canvasRef={canvasRef}
              color={color}
            />
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </div>
  );
};
